import { useCallback } from "react"
import { useMutation } from "urql"

import {
  CreateCustomerPurchaseOrderDocument,
  type CreateCustomerPurchaseOrderMutation,
  type CustomerPurchaseOrder,
  type CustomerPurchaseOrderCreateFailure,
  type CustomerPurchaseOrderCreateInput,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { PERMISSION_DENIED_MUTATION_MESSAGE, isForbiddenError } from "@/utils/urql/permissionError"

type CreateCpoResult =
  | { success: true }
  | { success: false; isDuplicate: true; duplicateCpo: CustomerPurchaseOrder }
  | { success: false; isDuplicate: false }

interface UseCreateCustomerPurchaseOrderReturn {
  createCustomerPurchaseOrder: (input: CustomerPurchaseOrderCreateInput) => Promise<CreateCpoResult>
  isCreating: boolean
}

export const useCreateCustomerPurchaseOrder = (): UseCreateCustomerPurchaseOrderReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    CreateCustomerPurchaseOrderMutation,
    { input: CustomerPurchaseOrderCreateInput }
  >(CreateCustomerPurchaseOrderDocument)

  const createCustomerPurchaseOrder = useCallback(
    async (input: CustomerPurchaseOrderCreateInput): Promise<CreateCpoResult> => {
      const { data, error } = await executeMutation({
        input: { ...input, confirmDuplicate: input.confirmDuplicate ?? false },
      })

      const output = data?.customerPurchaseOrder.create
      const hasError = error || output?.__typename === "CustomerPurchaseOrderCreateFailure"

      if (hasError) {
        const failure = output as CustomerPurchaseOrderCreateFailure

        if (failure?.error.isDuplicate && failure.error.existingCustomerPurchaseOrder) {
          return { success: false, isDuplicate: true, duplicateCpo: failure.error.existingCustomerPurchaseOrder }
        }

        const message = isForbiddenError(error)
          ? PERMISSION_DENIED_MUTATION_MESSAGE
          : (error?.message ?? failure.error.message)
        notify({ type: NotificationType.Error, message })
        return { success: false, isDuplicate: false }
      }

      notify({ type: NotificationType.Success, message: "Customer purchase order created" })
      return { success: true }
    },
    [executeMutation, notify]
  )

  return { createCustomerPurchaseOrder, isCreating: fetching }
}
